import React, { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

// ---------------------------------------------------------------------------
// PWAInstallPrompt — Android-Chrome-only install bottom sheet. Catches the
// `beforeinstallprompt` event, waits until the user has shown some interest
// (scrolls past hero or 20s elapsed), then shows a friendly install bottom
// sheet. Dismissal is remembered so we don't nag.
// ---------------------------------------------------------------------------
const DISMISS_KEY = "noi_pwa_install_dismissed_at";
const COOLDOWN_DAYS = 14;

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Never show if running already installed (standalone) or user dismissed recently
    const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator.standalone === true;
    if (isStandalone) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < COOLDOWN_DAYS * 24 * 60 * 60 * 1000) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Wait a bit before popping the sheet so we don't interrupt first-impression
      setTimeout(() => setShow(true), 4000);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "dismissed") localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setDeferred(null);
  };

  if (!show || !deferred) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden p-3 animate-[slideUp_.4s_ease-out]"
      role="dialog"
      aria-live="polite"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 flex items-start gap-3">
        <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-[#0B5ED7] to-[#20C997] grid place-items-center text-white">
          <Smartphone className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-slate-900 text-sm">Install NeuroOpinion</div>
          <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
            Get the app on your home screen — faster access, offline reports.
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={install}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#0B5ED7] hover:bg-[#084BB0] text-white text-xs font-semibold"
              data-testid="pwa-install-accept"
            >
              <Download className="w-3.5 h-3.5"/> Install
            </button>
            <button
              onClick={dismiss}
              className="h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium"
              data-testid="pwa-install-dismiss"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 shrink-0"
          aria-label="Close"
        >
          <X className="w-4 h-4"/>
        </button>
      </div>
      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
}
